// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { FooterContext } from "@insite/content-library/Pages/Footer";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LinkButton from "@mcmc/XDL/Components/Common/LinkButton";
import { layoutContainer } from "@mcmc/XDL/Utilities/sharedStyles";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface CreateEStoryAccountStyles {
    widgetWrapper: InjectableCss;
    containerWrapper: InjectableCss;
    createYourEStoreLoginTodayMessage: TypographyProps;
}

const styles: CreateEStoryAccountStyles = {
    widgetWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            background: ${getColor("common.eStoryAccount.main")};
            padding-top: 20px;
            padding-bottom: 20px;
        `,
    },
    containerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            flex-wrap: wrap;
            ${layoutContainer}
            & {
                margin: -5px -12.5px;
            }
        `,
    },
    createYourEStoreLoginTodayMessage: {
        color: "common.eStoryAccount.contrast",
        css: css`
            text-align: center;
            font-size: 25px;
            line-height: 29px;
            letter-spacing: 0.33;
            margin: 5px 12.5px;
        `,
    },
};

const mapStateToProps = (state: ApplicationState) => {
    const createAccountPageLink = getPageLinkByPageType(state, "CreateAccountPage");
    return {
        createAccountPageLink,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const CreateEStoryAccount: FC<Props> = ({ createAccountPageLink }) => {
    return (
        <StyledWrapper {...styles.widgetWrapper}>
            <StyledWrapper {...styles.containerWrapper}>
                <Typography {...styles.createYourEStoreLoginTodayMessage}>
                    {siteMessage("McMc_Create_Your_eStoreLogin_Today")}
                </Typography>
                <LinkButton
                    href={createAccountPageLink?.url}
                    sizeVariant="medium"
                    css={css`
                        margin: 5px 12.5px;
                    `}
                >
                    {translate("Create Account")}
                </LinkButton>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const createEStoryAccount: WidgetModule = {
    component: connect(mapStateToProps)(CreateEStoryAccount),
    definition: {
        group: "Footer",
        allowedContexts: [FooterContext],
    },
};

export default createEStoryAccount;
